
import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge, Printer } from 'lucide-react';

interface PrintableReferralCardProps {
  referralLink: string;
  referralCode: string;
  referrerName: string;
}

const PrintableReferralCard: React.FC<PrintableReferralCardProps> = ({
  referralLink,
  referralCode,
  referrerName
}) => {
  const handlePrint = () => {
    const printContent = document.getElementById('referral-card-printable');
    
    if (printContent) {
      const originalContents = document.body.innerHTML;
      
      const printStyles = `
        <style>
          @media print {
            body { margin: 0; padding: 0; }
            .referral-card-print {
              width: 3.5in;
              height: 2in;
              padding: 0.15in;
              border: 1px solid #ccc;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              page-break-inside: avoid;
              margin: 0 auto;
              overflow: hidden;
            }
            .referral-card-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 0.03in;
            }
            .referral-card-logo {
              font-weight: bold;
              font-size: 12pt;
              color: #8B5CF6;
            }
            .referral-card-code {
              font-size: 10pt;
              font-weight: bold;
              letter-spacing: 1px;
              color: #8B5CF6;
              max-width: 1.5in;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .referral-card-message {
              font-size: 9pt;
              text-align: center;
              margin-bottom: 0.03in;
            }
            .referral-card-qr {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              margin-bottom: 0.08in;
            }
            .referral-card-footer {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 0.03in;
              margin-top: auto;
              margin-bottom: 0.03in;
            }
            .referral-card-name {
              font-size: 9pt;
              text-align: center;
              color: #4B5563;
              max-width: 3in;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .referral-card-url {
              font-size: 8pt;
              text-align: center;
              color: #6B7280;
              max-width: 3in;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        </style>
      `;
      
      document.body.innerHTML = printStyles + printContent.outerHTML;
      window.print();
      document.body.innerHTML = originalContents;
    }
  };
  
  return (
    <div>
      <Card className="bg-white shadow-md mb-4">
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Business Card</h3>
            <Button onClick={handlePrint} variant="outline" size="sm" className="flex items-center gap-2">
              <Printer className="h-4 w-4" />
              Print
            </Button>
          </div>
          
          <div id="referral-card-printable" className="border border-gray-300 rounded-md p-4 bg-white">
            <div className="referral-card-print">
              <div className="referral-card-header">
                <div className="referral-card-logo flex items-center">
                  <Badge className="h-4 w-4 text-primary-blue mr-1" />
                  <span className="truncate max-w-[120px]">Referral Rewards</span>
                </div>
                <div className="referral-card-code truncate max-w-[100px]">
                  {referralCode}
                </div>
              </div>
              
              <div className="referral-card-message">
                <p className="text-xs">Please refer me! My link is below:</p>
              </div>
              
              <div className="referral-card-qr">
                <QRCodeSVG 
                  value={referralLink}
                  size={70}
                  bgColor={"#ffffff"}
                  fgColor={"#000000"}
                  level={"L"}
                  includeMargin={false}
                />
              </div>
              
              <div className="referral-card-footer">
                <div className="referral-card-name truncate max-w-[200px]">
                  Recommended by {referrerName}
                </div>
                <div className="referral-card-url truncate max-w-[200px] text-[10px]">
                  {referralLink}
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-gray-500 mt-4">
            Prints a standard business card size (3.5" × 2") with your referral details.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrintableReferralCard;
